import { useEffect, useState } from "react";
import type { AdvertisingBudgetDto } from "@/api/advertising";
import { extractErrorMessage } from "@/api/client";
import { Modal } from "@/components/ui/Modal";
import { Button, Input, Label } from "@/design-system/components/ui";
import { useSaveAdvertisingBudget } from "@/hooks/buyers/useAdvertising";
import { periodLabel, periodStartIso } from "./advertisingBits";

interface Props {
  open: boolean;
  onClose: () => void;
  buyerTeamId: string;
  /** `YYYY-MM`. */
  period: string;
  /** The month's existing budget, when one has been set. */
  budget: AdvertisingBudgetDto | null;
}

/**
 * Set one team's advertising budget for one month (D9).
 *
 * <p>🔴 <b>A budget FLAGS, it never BLOCKS.</b> Recording spend that pushes the
 * month over is accepted; the over-budget signal lives on the read side. The
 * money left somebody's bank account in the real world, and a system that
 * refused to write it down would leave the ledger describing a month that did
 * not happen — and would teach operators to date spend into the wrong month to
 * get past the guard.</p>
 *
 * <p>Gated <c>BuyerAdvertising.ManageBudget</c>, deliberately narrower than
 * <c>ManageAds</c>: the manager who decides what to spend on should not also be
 * able to raise their own ceiling.</p>
 */
export function BudgetEditorModal({ open, onClose, buyerTeamId, period, budget }: Props) {
  const [amount, setAmount] = useState("");
  const [notes, setNotes] = useState("");
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    setError(null);
    setAmount(budget ? String(budget.amount) : "");
    setNotes(budget?.notes ?? "");
  }, [open, budget]);

  const save = useSaveAdvertisingBudget();

  const parsed = amount.trim() === "" ? null : Number(amount);
  // Zero is legal and means "spend nothing this month", which is an
  // instruction. Negative is not a smaller budget, it is a nonsense one.
  const valid = parsed !== null && Number.isFinite(parsed) && parsed >= 0;

  async function submit() {
    setError(null);
    try {
      await save.mutateAsync({
        buyerTeamId,
        periodStart: periodStartIso(period),
        amount: parsed!,
        notes: notes.trim() || null,
      });
      onClose();
    } catch (e) {
      setError(extractErrorMessage(e));
    }
  }

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={`Advertising budget — ${periodLabel(period)}`}
      size="sm"
      footer={
        <div className="flex justify-end gap-space-2">
          <Button variant="secondary" onClick={onClose} disabled={save.isPending}>
            Cancel
          </Button>
          <Button onClick={submit} disabled={!valid || save.isPending}>
            {save.isPending ? "Saving…" : "Save budget"}
          </Button>
        </div>
      }
    >
      <div className="space-y-space-3">
        <div>
          <Label htmlFor="budget-amount">Amount</Label>
          <Input
            id="budget-amount" type="text" inputMode="decimal" value={amount}
            onChange={(e) => setAmount(e.target.value)}
            placeholder="0.00"
          />
          <p className="mt-space-1 text-caption text-gray-mid">
            A ceiling to measure against, not a limit the system enforces —
            spend over it is recorded and flagged, never refused.
          </p>
        </div>

        <div>
          <Label htmlFor="budget-notes">Notes</Label>
          <Input
            id="budget-notes" value={notes} onChange={(e) => setNotes(e.target.value)} />
        </div>

        {error && <p role="alert" className="text-caption text-status-error">{error}</p>}
      </div>
    </Modal>
  );
}
